import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Tooltip } from 'vnetwork-ui'
import { Save, Send } from 'lucide-react'
import { MailerCampaignSharedVariable } from '../../../types'

interface ActionBarProps {
    isNew: boolean
    receiversCount: number
    isSubmitting: boolean
    campaignStatus?: string
    sourceType: 'internal' | 'external'
    internalMode?: 'preset' | 'filters'
    filtersPreviewTotal?: number
    excludedCount?: number
    sharedVariables: MailerCampaignSharedVariable[]
    smtpEnabled: boolean
    smtpTested: boolean
    isStartingDelivery: boolean
    onCancel: () => void
    onSubmit: () => void
    onBulkDelivery: () => void
}

const ActionBar: React.FC<ActionBarProps> = ({
    isNew,
    receiversCount,
    isSubmitting,
    campaignStatus,
    sourceType,
    internalMode,
    filtersPreviewTotal,
    excludedCount = 0,
    sharedVariables,
    smtpEnabled,
    smtpTested,
    isStartingDelivery,
    onCancel,
    onSubmit,
    onBulkDelivery,
}) => {
    const { t } = useTranslation()

    const isDraft = campaignStatus === 'draft'

    // Check missing shared variables
    const missingSharedVars = useMemo(
        () => sharedVariables.filter((v) => !v.value || v.value.trim() === ''),
        [sharedVariables]
    )

    // Validation for save
    const saveValidation = useMemo(() => {
        const warnings: string[] = []

        if (missingSharedVars.length > 0) {
            warnings.push(
                t('mailerCampaign:shared_variables_not_filled', {
                    count: missingSharedVars.length,
                })
            )
        }

        return {
            hasWarnings: warnings.length > 0,
            warnings,
        }
    }, [missingSharedVars, t])

    // Get effective receivers count based on source type (minus excluded)
    const effectiveReceiversCount = useMemo(() => {
        if (sourceType === 'internal' && internalMode === 'filters') {
            return (filtersPreviewTotal ?? 0) - excludedCount
        }
        return receiversCount
    }, [
        sourceType,
        internalMode,
        filtersPreviewTotal,
        excludedCount,
        receiversCount,
    ])

    // Validation for delivery
    const deliveryValidation = useMemo(() => {
        const errors: string[] = []

        if (isNew) {
            errors.push(t('mailerCampaign:save_campaign_first'))
        }

        if (sourceType === 'external' && receiversCount === 0) {
            errors.push(t('mailerCampaign:no_receivers_imported'))
        }

        // For internal + filters: check effective receivers (total - excluded)
        if (sourceType === 'internal' && internalMode === 'filters') {
            if (effectiveReceiversCount <= 0) {
                errors.push(t('mailerCampaign:no_receivers_found'))
            }
        }

        if (missingSharedVars.length > 0) {
            errors.push(
                t('mailerCampaign:shared_variables_not_filled', {
                    count: missingSharedVars.length,
                })
            )
        }

        if (smtpEnabled && !smtpTested) {
            errors.push(t('mailerCampaign:smtp_not_tested'))
        }

        return {
            canDelivery: errors.length === 0,
            errors,
        }
    }, [
        isNew,
        sourceType,
        internalMode,
        receiversCount,
        effectiveReceiversCount,
        missingSharedVars,
        smtpEnabled,
        smtpTested,
        t,
    ])

    const deliveryButton = (
        <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<Send size={16} />}
            onClick={onBulkDelivery}
            disabled={
                !deliveryValidation.canDelivery ||
                isStartingDelivery ||
                isSubmitting
            }
        >
            {isDraft
                ? t('mailerCampaign:start_delivery')
                : t('mailerCampaign:resend')}
        </Button>
    )

    return (
        <>
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
                <div className="w-full px-6 py-3 flex items-center justify-end gap-3">
                    {effectiveReceiversCount > 0 && (
                        <span className="text-sm text-gray-500 mr-auto">
                            {t('mailerCampaign:receivers')}:{' '}
                            {effectiveReceiversCount}
                        </span>
                    )}
                    <Button variant="outlined" onClick={onCancel}>
                        {t('cancel')}
                    </Button>
                    {saveValidation.hasWarnings ? (
                        <Tooltip
                            title={saveValidation.warnings.join('\n')}
                            placement="top"
                        >
                            <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                startIcon={<Save size={16} />}
                                onClick={onSubmit}
                                disabled={isSubmitting}
                            >
                                {isNew
                                    ? t('mailerCampaign:create_campaign')
                                    : t('save_changes')}
                            </Button>
                        </Tooltip>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Save size={16} />}
                            onClick={onSubmit}
                            disabled={isSubmitting}
                        >
                            {isNew
                                ? t('mailerCampaign:create_campaign')
                                : t('save_changes')}
                        </Button>
                    )}
                    {!isNew &&
                        (deliveryValidation.canDelivery ? (
                            deliveryButton
                        ) : (
                            <Tooltip
                                title={deliveryValidation.errors.join('\n')}
                                placement="top"
                            >
                                <span>{deliveryButton}</span>
                            </Tooltip>
                        ))}
                </div>
            </div>
            {/* Spacer for fixed bottom bar */}
            <div className="h-16" />
        </>
    )
}

export default ActionBar
